import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  MessageCircle,
  PenSquare,
  Star,
  UserRound,
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardContent,
  Loading,
} from '@/components/ui';
import { ReviewComposerBottomSheet } from '@/components/reviews/ReviewComposerBottomSheet';
import { useAuthStore } from '@/stores/authStore';
import { useMessageStore } from '@/stores/messageStore';
import {
  useReviewStore,
  type ReviewableConversation,
} from '@/stores/reviewStore';
import { useToastStore } from '@/stores/toastStore';
import { formatRelativeTime } from '@kulum/shared';

type ReviewTab = 'received' | 'write';

interface TargetUserSummary {
  displayName: string;
  iconUrl?: string;
}

export function ReviewsPage() {
  const [searchParams] = useSearchParams();
  const user = useAuthStore((state) => state.user);
  const userData = useAuthStore((state) => state.userData);
  const conversations = useMessageStore((state) => state.conversations);
  const addToast = useToastStore((state) => state.addToast);

  const receivedReviews = useReviewStore((state) => state.receivedReviews);
  const sentReviews = useReviewStore((state) => state.sentReviews);
  const reviewableConversations = useReviewStore((state) => state.reviewableConversations);
  const receivedStats = useReviewStore((state) => state.receivedStats);
  const isLoadingReceived = useReviewStore((state) => state.isLoadingReceived);
  const isLoadingSent = useReviewStore((state) => state.isLoadingSent);
  const isLoadingReviewable = useReviewStore((state) => state.isLoadingReviewable);
  const isSubmitting = useReviewStore((state) => state.isSubmitting);
  const error = useReviewStore((state) => state.error);
  const fetchReceivedReviews = useReviewStore((state) => state.fetchReceivedReviews);
  const fetchSentReviews = useReviewStore((state) => state.fetchSentReviews);
  const fetchReviewableConversations = useReviewStore((state) => state.fetchReviewableConversations);
  const submitReview = useReviewStore((state) => state.submitReview);
  const clearError = useReviewStore((state) => state.clearError);

  const [activeTab, setActiveTab] = useState<ReviewTab>('received');
  const [targetUser, setTargetUser] = useState<TargetUserSummary | null>(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<ReviewableConversation | null>(null);
  const [rating, setRating] = useState<1 | 2 | 3 | 4 | 5>(5);
  const [comment, setComment] = useState('');

  const targetUserId = searchParams.get('userId') || user?.uid || null;
  const isOwnPage = !!user?.uid && targetUserId === user.uid;
  const isDoneeWithoutIdentity = userData?.role === 'donee' && userData.identityVerified !== true;
  const pageTitle = isOwnPage
    ? 'レビュー管理'
    : `${targetUser?.displayName || 'ユーザー'}さんのレビュー`;

  useEffect(() => {
    const loadTargetUser = async () => {
      if (!targetUserId) return;
      try {
        const userSnap = await getDoc(doc(db, 'users', targetUserId));
        if (!userSnap.exists()) {
          setTargetUser({
            displayName: '不明なユーザー',
          });
          return;
        }
        const data = userSnap.data() as { displayName?: unknown; iconUrl?: unknown };
        setTargetUser({
          displayName:
            typeof data.displayName === 'string' && data.displayName
              ? data.displayName
              : '不明なユーザー',
          iconUrl: typeof data.iconUrl === 'string' ? data.iconUrl : undefined,
        });
      } catch (loadError) {
        console.error('Error loading target user:', loadError);
      }
    };

    void loadTargetUser();
  }, [targetUserId]);

  useEffect(() => {
    if (!targetUserId) return;
    void fetchReceivedReviews(targetUserId);
  }, [targetUserId, fetchReceivedReviews]);

  useEffect(() => {
    if (!isOwnPage || !user?.uid) {
      return;
    }

    void fetchSentReviews(user.uid);
    if (!isDoneeWithoutIdentity) {
      void fetchReviewableConversations(user.uid, conversations);
    }
  }, [
    isOwnPage,
    user?.uid,
    isDoneeWithoutIdentity,
    conversations,
    fetchSentReviews,
    fetchReviewableConversations,
  ]);

  const distributionRows = useMemo(() => [5, 4, 3, 2, 1] as const, []);
  const showLoading = isLoadingReceived || (isOwnPage && (isLoadingSent || isLoadingReviewable));

  const handleOpenReviewModal = useCallback(
    (conversation: ReviewableConversation) => {
      if (isDoneeWithoutIdentity) {
        addToast('warning', 'レビュー投稿には本人確認が必要です');
        return;
      }
      setSelectedConversation(conversation);
      setRating(5);
      setComment('');
      clearError();
      setIsReviewModalOpen(true);
    },
    [clearError, isDoneeWithoutIdentity, addToast]
  );

  const handleCloseReviewModal = useCallback(() => {
    setIsReviewModalOpen(false);
    setSelectedConversation(null);
    setComment('');
    setRating(5);
    clearError();
  }, [clearError]);

  const handleSubmitReview = useCallback(async () => {
    if (!user?.uid || !selectedConversation) return;
    if (isDoneeWithoutIdentity) {
      addToast('warning', 'レビュー投稿には本人確認が必要です');
      return;
    }

    try {
      await submitReview({
        fromUserId: user.uid,
        toUserId: selectedConversation.otherUserId,
        conversationId: selectedConversation.conversationId,
        rating,
        comment: comment.trim(),
      });
      addToast('success', 'レビューを投稿しました');
      handleCloseReviewModal();
      await fetchSentReviews(user.uid);
      await fetchReviewableConversations(user.uid, conversations);
    } catch (submitError) {
      const message =
        submitError instanceof Error && submitError.message === 'already-reviewed'
          ? 'この会話のレビューは投稿済みです'
          : 'レビューの投稿に失敗しました';
      addToast('error', message);
    }
  }, [
    user?.uid,
    selectedConversation,
    rating,
    comment,
    submitReview,
    addToast,
    handleCloseReviewModal,
    fetchSentReviews,
    fetchReviewableConversations,
    conversations,
    isDoneeWithoutIdentity,
  ]);

  const renderStarRow = (value: number, size: 'sm' | 'lg' = 'sm') => (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, index) => {
        const starNumber = index + 1;
        return (
          <Star
            key={starNumber}
            className={`${size === 'lg' ? 'h-6 w-6' : 'h-4 w-4'} ${
              starNumber <= value ? 'fill-copper text-copper' : 'text-neutral-300'
            }`}
          />
        );
      })}
    </div>
  );

  return (
    <>
      <div className="min-h-full bg-[#F7F4EF]">
        <div className="md:mx-auto md:max-w-3xl md:px-6 md:py-6">
          <div className="px-4 pb-12 pt-14 md:px-0 md:pt-0">
            <Link
              to="/mypage"
              className="mb-5 inline-flex items-center gap-2 text-[13px] text-[#7E7668] transition hover:text-copper"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>マイページに戻る</span>
            </Link>

            <div className="mb-6 flex items-center justify-between gap-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#B7B0A4]">Review</p>
                <h1 className="mt-1 text-[22px] font-semibold text-[#1A1A1A]">{pageTitle}</h1>
              </div>
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#F5EDE2]">
                <Star className="h-5 w-5 text-copper" />
              </div>
            </div>

            {!isOwnPage && (
              <Card className="mb-5 overflow-hidden border-[#E8DCCB] bg-[linear-gradient(135deg,#F8EDD9_0%,#FCF6ED_55%,#FFFFFF_100%)]">
                <CardContent className="flex items-center gap-3 px-4 py-4">
                  <Avatar
                    src={targetUser?.iconUrl}
                    alt={targetUser?.displayName || 'ユーザー'}
                    size="sm"
                    className="border-2 border-white shadow-[0_4px_10px_rgba(0,0,0,0.08)]"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] font-semibold text-[#1A1A1A]">
                      {targetUser?.displayName || 'ユーザー'}
                    </p>
                    <p className="mt-0.5 text-[12px] text-[#8D877D]">このユーザーに届いたレビュー一覧</p>
                  </div>
                  <Link to="/mypage/reviews">
                    <Button size="sm" variant="secondary" className="!rounded-full !px-3.5">
                      自分の管理
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}

            {showLoading ? (
              <div className="py-8">
                <Loading />
              </div>
            ) : (
              <>
                <Card className="mb-6 overflow-hidden border-[#ECE6DD]">
                  <CardContent className="px-5 py-5">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="mb-1 text-[13px] text-[#8D877D]">総合評価</p>
                        <div className="flex items-end gap-2">
                          <span className="text-3xl font-bold text-[#1A1A1A]">
                            {receivedStats.averageRating.toFixed(1)}
                          </span>
                          <span className="pb-1 text-sm text-[#8D877D]">/ 5.0</span>
                        </div>
                        <p className="mt-1 text-sm text-[#8D877D]">{receivedStats.totalCount}件のレビュー</p>
                      </div>
                      {renderStarRow(Math.round(receivedStats.averageRating), 'lg')}
                    </div>

                    <div className="mt-4 space-y-2">
                      {distributionRows.map((star) => {
                        const count = receivedStats.distribution[star];
                        const ratio = receivedStats.totalCount > 0
                          ? (count / receivedStats.totalCount) * 100
                          : 0;
                        return (
                          <div key={star} className="flex items-center gap-3">
                            <span className="w-6 text-sm text-[#726C63]">{star}</span>
                            <Star className="h-3 w-3 fill-copper text-copper" />
                            <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#EFEAE2]">
                              <div className="h-full bg-copper/70" style={{ width: `${ratio}%` }} />
                            </div>
                            <span className="w-8 text-right text-xs text-[#8D877D]">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {isOwnPage && (
                  <div className="mb-5 inline-flex rounded-full border border-[#E7DFD2] bg-white p-1">
                    <button
                      type="button"
                      onClick={() => setActiveTab('received')}
                      className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                        activeTab === 'received'
                          ? 'bg-copper text-white shadow-[0_4px_12px_rgba(184,115,51,0.28)]'
                          : 'text-[#8D877D] hover:bg-[#F7F3ED]'
                      }`}
                    >
                      受け取ったレビュー
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('write')}
                      className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                        activeTab === 'write'
                          ? 'bg-copper text-white shadow-[0_4px_12px_rgba(184,115,51,0.28)]'
                          : 'text-[#8D877D] hover:bg-[#F7F3ED]'
                      }`}
                    >
                      レビューを書く
                    </button>
                  </div>
                )}

                {(activeTab === 'received' || !isOwnPage) && (
                  <>
                    {receivedReviews.length === 0 ? (
                      <Card className="overflow-hidden border-[#ECE6DD]">
                        <CardContent className="py-16 text-center">
                          <Star className="mx-auto mb-4 h-14 w-14 text-neutral-300" />
                          <h2 className="mb-2 text-lg font-semibold text-[#1A1A1A]">まだレビューはありません</h2>
                          <p className="text-[#8D877D]">会話後に投稿されたレビューがここに表示されます。</p>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="space-y-3">
                        {receivedReviews.map((review) => (
                          <Card key={review.reviewId} className="overflow-hidden border-[#ECE6DD]">
                            <CardContent className="py-4">
                              <div className="flex items-start gap-3">
                                <Avatar
                                  src={review.fromUser?.iconUrl}
                                  alt={review.fromUser?.displayName || 'ユーザー'}
                                  size="sm"
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-start justify-between gap-2">
                                    <div>
                                      <p className="font-medium text-[#1A1A1A]">
                                        {review.fromUser?.displayName || '匿名ユーザー'}
                                      </p>
                                      <div className="mt-1">{renderStarRow(review.rating)}</div>
                                    </div>
                                    <span className="text-xs text-[#8D877D]">
                                      {formatRelativeTime(review.createdAt)}
                                    </span>
                                  </div>
                                  {review.comment && (
                                    <p className="mt-2 whitespace-pre-wrap text-sm text-neutral-700">
                                      {review.comment}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {isOwnPage && activeTab === 'write' && (
                  <div className="space-y-4">
                    {isDoneeWithoutIdentity && (
                      <Card className="overflow-hidden border-[#E5D4BD] bg-[#FBF5EC]">
                        <CardContent className="py-5">
                          <h2 className="mb-2 text-lg font-semibold text-[#1A1A1A]">本人確認が必要です</h2>
                          <p className="text-sm text-[#7E7668]">
                            ドニー会員は本人確認が完了するとレビューを投稿できます。
                          </p>
                          <Link to="/mypage/verification" className="mt-4 inline-flex">
                            <Button size="sm" className="!rounded-full">本人確認を申請する</Button>
                          </Link>
                        </CardContent>
                      </Card>
                    )}

                    <Card className="overflow-hidden border-[#ECE6DD]">
                      <CardContent className="py-5">
                        <h2 className="mb-2 text-lg font-semibold text-[#1A1A1A]">レビュー投稿候補</h2>
                        <p className="mb-4 text-sm text-[#8D877D]">
                          会話済みで、まだレビューしていない相手を表示しています。
                        </p>

                        {isDoneeWithoutIdentity ? (
                          <div className="py-10 text-center">
                            <MessageCircle className="mx-auto mb-3 h-12 w-12 text-neutral-300" />
                            <p className="text-[#8D877D]">本人確認完了後に投稿候補が表示されます</p>
                          </div>
                        ) : reviewableConversations.length === 0 ? (
                          <div className="py-10 text-center">
                            <MessageCircle className="mx-auto mb-3 h-12 w-12 text-neutral-300" />
                            <p className="text-[#8D877D]">投稿可能な相手がいません</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {reviewableConversations.map((conversation) => (
                              <div
                                key={conversation.conversationId}
                                className="flex items-center gap-3 rounded-xl border border-[#EFEAE2] bg-[#FCFBF9] p-3"
                              >
                                <Avatar
                                  src={conversation.iconUrl}
                                  alt={conversation.displayName}
                                  size="sm"
                                />
                                <div className="min-w-0 flex-1">
                                  <p className="truncate font-medium text-[#1A1A1A]">
                                    {conversation.displayName}
                                  </p>
                                  {conversation.lastMessageAt && (
                                    <p className="text-xs text-[#8D877D]">
                                      最終メッセージ: {formatRelativeTime(conversation.lastMessageAt)}
                                    </p>
                                  )}
                                </div>
                                <Button
                                  size="sm"
                                  className="!rounded-full"
                                  leftIcon={<PenSquare className="h-4 w-4" />}
                                  onClick={() => handleOpenReviewModal(conversation)}
                                >
                                  投稿
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    <Card className="overflow-hidden border-[#ECE6DD]">
                      <CardContent className="py-5">
                        <h2 className="mb-3 text-lg font-semibold text-[#1A1A1A]">投稿済みレビュー</h2>
                        {sentReviews.length === 0 ? (
                          <p className="text-sm text-[#8D877D]">まだ投稿したレビューはありません。</p>
                        ) : (
                          <div className="space-y-3">
                            {sentReviews.map((review) => (
                              <div
                                key={review.reviewId}
                                className="rounded-xl border border-[#EFEAE2] bg-[#FCFBF9] p-3"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex items-center gap-2">
                                    <UserRound className="h-4 w-4 text-neutral-500" />
                                    <p className="font-medium text-[#1A1A1A]">
                                      {review.toUser?.displayName || '不明なユーザー'}
                                    </p>
                                  </div>
                                  <Badge variant="info">{formatRelativeTime(review.createdAt)}</Badge>
                                </div>
                                <div className="mt-2">{renderStarRow(review.rating)}</div>
                                {review.comment && (
                                  <p className="mt-2 whitespace-pre-wrap text-sm text-neutral-700">
                                    {review.comment}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <ReviewComposerBottomSheet
        isOpen={isReviewModalOpen}
        onClose={handleCloseReviewModal}
        onSubmit={handleSubmitReview}
        isSubmitting={isSubmitting}
        error={error}
        targetUser={
          selectedConversation
            ? {
                displayName: selectedConversation.displayName,
                iconUrl: selectedConversation.iconUrl,
              }
            : null
        }
        rating={rating}
        onRatingChange={setRating}
        comment={comment}
        onCommentChange={setComment}
        maxCommentLength={500}
      />
    </>
  );
}
